import { useState } from 'react';
import { toast } from 'sonner';
import { GitCompare } from 'lucide-react';

import {
  signalName,
  type SignalVersion,
  useSignalVersionsQuery,
  useVersionChangeMutation,
  useVersionCompareQuery,
} from '@/client/api/assets/signals';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const STATUS_CLS: Record<string, string> = {
  active: 'bg-green-100 text-green-700', candidate: 'bg-purple-100 text-purple-700',
  previous: 'bg-gray-100 text-gray-600', retired: 'bg-gray-100 text-gray-400',
};

/** b − a in 21 buckets of 10; the middle bucket is "no change". */
const Deltas = ({ buckets }: { buckets: number[] }) => {
  const max = Math.max(...buckets, 1);
  return (
    <div className="flex h-8 items-end gap-px" title="Score change b − a, −100 … +100">
      {buckets.map((n, i) => (
        <div key={i} className={cn('w-1.5 rounded-sm', i === 10 ? 'bg-gray-400' : i < 10 ? 'bg-amber-400' : 'bg-emerald-400')}
          style={{ height: `${Math.max((n / max) * 100, n ? 6 : 2)}%`, opacity: n ? 1 : 0.25 }} />
      ))}
    </div>
  );
};

const CompareDialog = ({ processor, a, b, onClose }: { processor: string; a: string; b: string; onClose: () => void }) => {
  const { data, isLoading, isError, error } = useVersionCompareQuery(processor, a, b);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{a} → {b}</DialogTitle>
          <DialogDescription>
            How {b} differs from {a} on the photos both have a value for (latest value each version wrote, from history).
          </DialogDescription>
        </DialogHeader>
        {isLoading ? <Skeleton className="h-40" /> : isError ? (
          <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not compare')}</p>
        ) : !data?.assets_compared ? (
          <p className="text-sm text-gray-600">
            No photo has values from both versions yet. Run a shadow job with the newer code on a sample, then compare again.
          </p>
        ) : (
          <div className="space-y-4 text-sm">
            <p>{data.assets_compared.toLocaleString()} photos compared.</p>
            <table className="w-full text-xs">
              <thead className="text-left text-gray-500"><tr><th className="py-1">Signal</th><th>Compared</th><th>Same label</th><th>Label changes</th><th>Score change</th></tr></thead>
              <tbody className="divide-y">
                {Object.entries(data.keys).map(([key, k]) => (
                  <tr key={key}>
                    <td className="py-1.5 font-medium">{signalName(key)}</td>
                    <td>{k.compared}</td>
                    <td className={cn(k.agreement != null && k.agreement < 0.9 && 'font-semibold text-amber-700')}>
                      {k.agreement == null ? '—' : `${Math.round(k.agreement * 100)}%`}
                    </td>
                    <td>{Object.entries(k.flips).slice(0, 4).map(([f, n]) => `${f} ${n}`).join(' · ') || '—'}</td>
                    <td><Deltas buckets={k.delta_histogram} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.flipped_sample.length > 0 && (
              <div>
                <div className="mb-1 font-medium">Photos whose label changed</div>
                <ul className="grid gap-1 sm:grid-cols-2">
                  {data.flipped_sample.map((f) => (
                    <li key={`${f.asid}-${f.signal_key}`} className="text-xs">
                      <a className="text-blue-600 hover:underline" href={`/admin/assets?q=${f.asid}`} target="_blank" rel="noreferrer">#{f.asid}</a>{' '}
                      {signalName(f.signal_key)}: {f.a.label ?? f.a.value} → {f.b.label ?? f.b.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Versions of one processor: what is active, what the deployed code stamps,
 * how many photos hold each version's values. Compare any version with the
 * active one; promote, roll back (promote + restore values from history) or
 * retire with a reason.
 */
const SignalVersionsPanel = ({ processor, onShadowRun }: { processor: string; onShadowRun?: () => void }) => {
  const { data, isLoading } = useSignalVersionsQuery();
  const change = useVersionChangeMutation();
  const { hasPermission, isSuperAdmin } = useHasPermission('asset.signals.manage');
  const canManage = hasPermission || isSuperAdmin;
  const [compare, setCompare] = useState<string | null>(null);
  const [pending, setPending] = useState<{ v: SignalVersion; verb: 'promote' | 'retire' | 'rollback' } | null>(null);
  const [reason, setReason] = useState('');

  const versions = (data?.versions ?? []).filter((v) => v.processor === processor);
  const active = versions.find((v) => v.status === 'active');
  const running = versions.find((v) => v.running);
  if (isLoading) return <Skeleton className="h-16" />;
  if (!versions.length) return null;

  const go = async () => {
    if (!pending || !reason.trim()) return;
    try {
      const out = await change.mutateAsync({ svid: pending.v.svid, verb: pending.verb, reason: reason.trim() });
      toast.success(pending.verb === 'rollback' ? `Rolled back to ${pending.v.label}; restore job #${out?.job?.ajid ?? out?.data?.job?.ajid ?? ''} started` : `${pending.v.label} ${pending.verb === 'promote' ? 'is now active' : 'retired'}`);
      setPending(null);
      setReason('');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not change the version'));
    }
  };

  return (
    <div className="border-t px-4 py-3">
      <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
        <span className="font-semibold uppercase tracking-wide">Versions</span>
        {running && active && running.label !== active.label && (
          <span className="text-purple-700">Deployed code is {running.label}; active is {active.label}.</span>
        )}
        {onShadowRun && running && active && running.label !== active.label && (
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={onShadowRun}>Shadow run…</Button>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="text-left text-gray-500">
          <tr className="[&>th]:px-2"><th className="py-1">Label</th><th>Status</th><th className="text-right">Photos now</th><th className="text-right">In history</th><th className="text-right">Shadow</th><th>First seen</th><th /></tr>
        </thead>
        <tbody className="divide-y">
          {versions.map((v) => (
            <tr key={v.svid} className="[&>td]:px-2" title={v.inputs ? JSON.stringify(v.inputs) : v.notes ?? undefined}>
              <td className="py-1.5 font-mono">{v.label}{v.running && <span className="ml-1 rounded bg-blue-50 px-1 text-[10px] text-blue-700">deployed</span>}</td>
              <td><span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', STATUS_CLS[v.status])}>{v.status}</span></td>
              <td className="text-right tabular-nums">{v.current_assets.toLocaleString()}</td>
              <td className="text-right tabular-nums">{v.history_assets.toLocaleString()}</td>
              <td className="text-right tabular-nums">{v.shadow_assets.toLocaleString()}</td>
              <td className="text-gray-500">{v.first_seen ? new Date(v.first_seen).toLocaleDateString() : '—'}</td>
              <td className="space-x-1 text-right">
                {active && v.label !== active.label && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setCompare(v.label)}>
                    <GitCompare className="mr-1 h-3 w-3" /> Compare
                  </Button>
                )}
                {canManage && v.status !== 'active' && v.status !== 'retired' && (
                  <>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setPending({ v, verb: 'promote' })}>Promote</Button>
                    {v.history_assets > 0 && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setPending({ v, verb: 'rollback' })}>Roll back</Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500" onClick={() => setPending({ v, verb: 'retire' })}>Retire</Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {compare && active && <CompareDialog processor={processor} a={active.label} b={compare} onClose={() => setCompare(null)} />}

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending?.verb === 'rollback' ? 'Roll back to' : pending?.verb === 'promote' ? 'Promote' : 'Retire'} {pending?.v.label}?</DialogTitle>
            <DialogDescription>
              {pending?.verb === 'promote' && 'It becomes the version "stale" is measured against: photos with values from any other version become stale, and a stale backfill re-runs them.'}
              {pending?.verb === 'rollback' && `It becomes active and a job writes its last values back for ${pending.v.history_assets.toLocaleString()} photos, from history, with no analysis calls.`}
              {pending?.verb === 'retire' && 'Marked as no longer in use. Its values and history stay.'}
            </DialogDescription>
          </DialogHeader>
          <Input placeholder="Reason (required, audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
            <Button disabled={!reason.trim() || change.isPending} onClick={() => void go()}>Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignalVersionsPanel;
